import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Settings2, GripVertical } from 'lucide-react';
import { apiFetch } from '../utils/api';

export function useColumnConfig(prefKey, defaultColumns) {
  const [columns, setColumns] = useState(defaultColumns);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiFetch(`/api/user/preferences/${prefKey}`);
        if (res.ok) {
          const { value } = await res.json();
          if (value?.order && value?.hidden) {
            // Merge saved prefs with defaults (handle new columns added since last save)
            const savedOrder = value.order;
            const savedHidden = new Set(value.hidden);
            const defaultIds = defaultColumns.map(c => c.id);
            const merged = [];
            // Add saved columns that still exist
            savedOrder.forEach(id => {
              const def = defaultColumns.find(c => c.id === id);
              if (def) merged.push({ ...def, visible: !savedHidden.has(id) });
            });
            // Add any new columns not in saved order
            defaultIds.forEach(id => {
              if (!savedOrder.includes(id)) {
                const def = defaultColumns.find(c => c.id === id);
                if (def) merged.push({ ...def });
              }
            });
            setColumns(merged);
          }
        }
      } catch (err) { console.error(err); }
      setLoaded(true);
    };
    load();
  }, [prefKey]);

  const save = useCallback(async (newCols) => {
    setColumns(newCols);
    try {
      await apiFetch(`/api/user/preferences/${prefKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          value: {
            order: newCols.map(c => c.id),
            hidden: newCols.filter(c => !c.visible).map(c => c.id),
          },
        }),
      });
    } catch (err) { console.error(err); }
  }, [prefKey]);

  const visibleColumns = columns.filter(c => c.visible);

  return { columns, setColumns: save, visibleColumns, loaded };
}

export function ColumnConfigPopover({ columns, onChange }) {
  const dragItem = useRef(null);
  const dragOver = useRef(null);
  const [localCols, setLocalCols] = useState(columns);

  useEffect(() => {
    setLocalCols(columns);
  }, [columns]);

  const handleDragStart = (idx) => {
    dragItem.current = idx;
  };

  const handleDragEnter = (idx) => {
    dragOver.current = idx;
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOver.current === null) return;
    const updated = [...localCols];
    const dragged = updated.splice(dragItem.current, 1)[0];
    updated.splice(dragOver.current, 0, dragged);
    dragItem.current = null;
    dragOver.current = null;
    setLocalCols(updated);
    onChange(updated);
  };

  const toggleVisibility = (id) => {
    const updated = localCols.map(c =>
      c.id === id ? { ...c, visible: !c.visible } : c
    );
    // Prevent hiding all columns
    if (updated.filter(c => c.visible).length === 0) return;
    setLocalCols(updated);
    onChange(updated);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" data-testid="column-config-btn">
          <Settings2 className="w-3 h-3" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="end" data-testid="column-config-popover">
        <p className="text-xs font-medium text-muted-foreground px-2 pb-2">Drag to reorder, toggle to show/hide</p>
        <div className="space-y-0.5">
          {localCols.map((col, idx) => (
            <div
              key={col.id}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragEnter={() => handleDragEnter(idx)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent/50 cursor-grab active:cursor-grabbing select-none"
              data-testid={`col-config-${col.id}`}
            >
              <GripVertical className="w-3 h-3 text-muted-foreground shrink-0" />
              <Checkbox
                checked={col.visible}
                onCheckedChange={() => toggleVisibility(col.id)}
                className="shrink-0"
              />
              <span className="text-xs truncate">{col.label}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
